import { requestAdHoc } from './utils';
import constants from '../config/constants';

const localMessagingServiceUrl = `https://agent.sporttia.com:9001`;

export const OP_ERROR = {
    UNKNOWN: 0,
    UNAVAILABLE: 'Estado de la operación no disponible.',
    NO_CHANGE: 'Se ha producido un error: el dispositivo no dispone de cambio.',
    CANCELLED: 'Se ha cancelado la operación.',
    HARDWARE:
        'Error en el dispositivo; verifique si hay billetes o monedas atascados.',
};

const defaultQueryTimer = 3000;

interface Piece {
    level: number;
    value: string | number;
}

interface AvailablePieces {
    monedas: Piece[];
    billetes: Piece[];
}

interface CashdroResponse {
    code: number;
    data?: string;
    response?: {
        errorMessage: string;
        operation?: Record<string, unknown>;
    };
}

interface Operation {
    operationid?: string;
    state?: string;
    payInProgress?: string;
    payOutProgress?: string;
    total?: string;
    totalin?: string;
    totalout?: string;
    amountchangenotavailable?: string;
    withError?: string;
}

interface Device {
    type: string;
    pieces?: Array<{
        value: string;
        finishlevelrecycler: number;
        finishlevelcassette: number;
        startlevelrecycler?: number | string;
    }>;
}

interface QueryOperationData {
    operation: Operation;
    devices: Device[];
}

interface WhileQueryingParams {
    operationId: string;
    queryTimer?: number;
    updateOnlyOnce?: boolean;
}

interface PieceToFinish {
    value: string;
    level: string;
    isBillete: string;
}

export default class Cashdro {
    deviceIp: string;
    user: string;
    pass: string;
    deviceUrl: string;
    availablePieces: AvailablePieces;
    noRepeatkillSwitch: boolean;

    constructor(ip = '', user = '', pass = '') {
        this.deviceIp = ip || localStorage.getItem('CashdroIp') || '';
        this.user = user || localStorage.getItem('CashdroUser') || '';
        this.pass = pass || localStorage.getItem('CashdroPass') || '';

        if (!this.deviceIp || !this.user || !this.pass) {
            throw new Error(
                'Error de configuración: consulte la sección "Configuración", perstaña "Cashdro".',
            );
        }

        this.deviceUrl = `https://${this.deviceIp}/Cashdro3WS/index.php`;

        this.availablePieces = {
            monedas: [],
            billetes: [],
        };

        this.noRepeatkillSwitch = false;
    }

    async startOperation(
        type: string | number,
        params?: Record<string, unknown>,
    ): Promise<CashdroResponse> {
        const result = await requestAdHoc<CashdroResponse>(
            'POST',
            localMessagingServiceUrl,
            {
                method: 'GET',
                url: this.deviceUrl,
                params: {
                    operation: 'startOperation',
                    type,
                    name: this.user,
                    password: this.pass,
                    parameters: params ? JSON.stringify(params) : '',
                },
            },
        );

        /*
        {
          "code": <code>, si su valor es 1, no se ha producido error, en caso contrario se indica el código del error (valor negativo).
          -1 Usuario/password incorrectos.
          -2 CashDro ocupado con otra transacción.
          -3 Importe de la transacción incorrecto.
          -4 El usuario no tiene permiso para realizar la transacción.
          -99 Parámetros incorrectos.
          -1900 CashDro está cargando o actualizando.
          -998 CashDro está fuera de servicio. Revisar estado en la pantalla de diagnostico.
          -999 El servicio de CashDro está parado.
          "response": {
            "errorMessage": "none",
            "operation": {
              "operationId": "<operationId>"
            }
          }
        }

        LO DE ARRIBA ES MENTIRA LOL

        La respuesta real es tipo:
        {
            "code": 1,
            "data: "35" <- el operationId de verdad
        }

        PARECE QUE TODAS LAS RESPUESTAS DE LA DOC SON MENTIRA. SIEMPRE ES "data"

        */

        return result;
    }

    async confirmOperation(operationId: string): Promise<CashdroResponse> {
        const result = await requestAdHoc<CashdroResponse>(
            'POST',
            localMessagingServiceUrl,
            {
                method: 'GET',
                url: this.deviceUrl,
                params: {
                    operation: 'acknowledgeOperationId',
                    operationId,
                    name: this.user,
                    password: this.pass,
                },
            },
        );

        /*
        {
            "code": <code>,
            1 ok
            -1 Usuario/password incorrectos
            -2 Identificador de la transacción incorrecto
            -3 Parámetros incorrectos, transacción no iniciada.
            "response": {
                "errorMessage": "none"
            }
        }
        */

        return result;
    }

    async whileQueryingOperation(
        params: WhileQueryingParams,
        onFinished?: (data: QueryOperationData) => void,
        onError?: (error: string) => void,
        onUpdate?: () => void,
    ): Promise<void> {
        const { operationId, queryTimer, updateOnlyOnce } = params;

        this.noRepeatkillSwitch = false;

        if (!operationId) {
            console.log('Se requiere operationId');
        }

        const interval = setInterval(async () => {
            const result = await this.queryOperation(operationId).catch(
                (err: { error?: { msg?: string } }) => {
                    if (onError) {
                        onError(err?.error?.msg || String(err));
                    }
                },
            );

            if (!result) return;

            const { code } = result;

            // result.data viene como un string que representa un json
            const data = JSON.parse(result.data || '{}') as QueryOperationData;

            // operation son los datos de la operación
            // devices es el estado de los mecanismos internos (qué monedas tienen etc)
            const { operation, devices } = data;

            if (code === 1) {
                switch (operation?.state) {
                    case 'F': // fin, pagado
                        clearInterval(interval);
                        if (onFinished) {
                            onFinished({ operation, devices });
                        }
                        break;
                    // hay que esperar
                    case 'Q':
                        break;
                    case 'E':
                        if (onUpdate) {
                            if (
                                !updateOnlyOnce ||
                                (updateOnlyOnce && !this.noRepeatkillSwitch)
                            ) {
                                onUpdate();
                                this.noRepeatkillSwitch = true;
                            }
                        }
                        break;
                    case 'I': // Operación no confirmada con acknowledgeOperation
                    default: // no puede pasar en teoría
                        clearInterval(interval);
                        if (onError) {
                            onError(
                                'Operación no confirmada / error desconocido',
                            );
                        }
                        break;
                }
            }
        }, queryTimer || defaultQueryTimer);
    }

    async queryOperation(operationId: string): Promise<CashdroResponse> {
        const result = await requestAdHoc<CashdroResponse>(
            'POST',
            localMessagingServiceUrl,
            {
                method: 'GET',
                url: this.deviceUrl,
                params: {
                    operation: 'askOperation',
                    operationId,
                    name: this.user,
                    password: this.pass,
                },
            },
        );

        return result;

        /*
        {
          "code": <code>,
          "response": {
            "errorMessage": "none",
            "operation": {
              "operation": {
                "operationid": "<operationId>",
                "state": "<state>",
                    I = operación pendiente de ejecutar (acknowledge).
                    Q = la operación está en cola.
                    E = la operación está en ejecución.
                    F = la operación está finalizada.
                "payInProgress": "<payInProgress>",
                "payOutProgress": "<payOutProgress>",
                "total": "<total>",
                "totalin": "<totalin>",
                    Importe total introducido en CashDro. Siempre se indica multiplicado por 100. Por ejemplo, para un importe de 1,05 se indica 105.
                "totalout": "<-totalout>",
                    Importe total dispensado por CashDro. Siempre se indica multiplicado por 100 y en negativo.
                "amountchangenotavailable": "<amountchangenotavailable>"
                    Importe no devuelto al generarse un cambio no disponible.
                …
              },
              "devices": [
                ...
                {
                  "type": "<type>",
                  ...
                  "pieces": [
                    {
                      ...
                      "value": "<value>",
                      ...
                      "finishlevelrecycler": <finishlevelrecycler>,
                      "finishlevelcassette": <finishlevelcassette>,
                      ...
                    },
                    ...
                  ]
                },
                ...
              ],
              "messages": [],
              ...
              "withError": "<withError>",
                True/false. Indica si CashDro está operativo o en estado de error (ej . billete atascado,..).
              ...
            }
          }
        }
        */
    }

    async cancelOperation(operationId: string): Promise<CashdroResponse> {
        const result = await requestAdHoc<CashdroResponse>(
            'POST',
            localMessagingServiceUrl,
            {
                method: 'GET',
                url: this.deviceUrl,
                params: {
                    operation: 'finishOperation',
                    operationId,
                    type: 2,
                    name: this.user,
                    password: this.pass,
                },
            },
        );

        /*
        {
            "code":<code>,
            "response":{
                "errorMessage":"none"
            }
        }
        */

        return result;
    }

    async finishOperation(
        operationId: string,
        params: Record<string, unknown> | null = null,
        type = 1,
    ): Promise<CashdroResponse> {
        const result = await requestAdHoc<CashdroResponse>(
            'POST',
            localMessagingServiceUrl,
            {
                method: 'GET',
                url: this.deviceUrl,
                params: {
                    operation: 'finishOperation',
                    operationId,
                    name: this.user,
                    password: this.pass,
                    type,
                    params: params ? JSON.stringify(params) : '',
                },
            },
        );

        /*
        {
            "code":<code>,
            "response":{
                "errorMessage":"none"
            }
        }
        */

        return result;
    }

    async importOperation(operationId: string): Promise<CashdroResponse> {
        const result = await requestAdHoc<CashdroResponse>(
            'POST',
            localMessagingServiceUrl,
            {
                method: 'GET',
                url: this.deviceUrl,
                params: {
                    operation: 'setOperationImported',
                    operationId,
                    name: this.user,
                    password: this.pass,
                },
            },
        );

        /*
        {
            "code":<code>,
            "response":{
                "errorMessage":"none"
            }
        }
        */

        return result;
    }

    async checkCurrencyLevels(): Promise<CashdroResponse> {
        const result = await requestAdHoc<CashdroResponse>(
            'POST',
            localMessagingServiceUrl,
            {
                method: 'GET',
                url: this.deviceUrl,
                params: {
                    operation: 'getPiecesCurrency',
                    currencyId: 'EUR',
                    includeImages: 0,
                    levels: 1,
                    name: this.user,
                    password: this.pass,
                },
            },
        );

        /*
        {
            "code": 1,
            "response": {
            "errorMessage": "none",
            "operation": {
            "pieces": [
            {
                "CurrencyId": "EUR",
                "Value": "1",
                "Type": "1",
                "Destination": "1",
                "MinLevel": "1",
                "MaxLevel": "0",
                "DepositLevel": "27",
                "MaxPiecesExchange": "0",
                "State": "-1",
                "Image": "",
                "LevelRecycler": "28",
                "LevelCasete": "0",...
            },...
         ]
         }
         }
        }
        */

        return result;
    }

    /**
     * Guardar las piezas (monedas y billetes) actualmente disponibles en los recicladores (los dispositivos que pueden escupir dinero).
     * @returns void
     */
    setAvailablePieces({
        monedas = [],
        billetes = [],
    }: {
        monedas?: Device['pieces'];
        billetes?: Device['pieces'];
    }): void {
        if (monedas && monedas.length > 0) {
            this.availablePieces.monedas = monedas.map((pieza) => {
                const startLevel = parseInt(
                    String(pieza.startlevelrecycler || 0),
                );
                const finishLevel = parseInt(String(pieza.finishlevelrecycler));

                let level = finishLevel;

                if (finishLevel > startLevel) {
                    level = finishLevel - startLevel;
                }

                return {
                    level,
                    value: pieza.value,
                };
            });
        }

        if (billetes && billetes.length > 0) {
            this.availablePieces.billetes = billetes.map((pieza) => ({
                level: pieza.finishlevelrecycler,
                value: pieza.value,
            }));
        }

        console.log('piezas disponibles:');
        console.log(this.availablePieces);
    }

    /**
     * Devuelve una lista de las monedas/billetes necesarias para emitir un reintegro (sí, hay que especificar).
     * Se devuelven de entre las disponibles, de mayor a menor denominación.
     * @param amount
     * @returns {{monedas: *[], billetes: *[]}}
     */
    calculateNecessaryPieces(amount: number | string): AvailablePieces {
        const available: AvailablePieces = {
            monedas: this.availablePieces.monedas.sort(
                (a, b) => parseInt(String(b.value)) - parseInt(String(a.value)),
            ),
            billetes: this.availablePieces.billetes.sort(
                (a, b) => parseInt(String(b.value)) - parseInt(String(a.value)),
            ),
        };

        const result: AvailablePieces = {
            monedas: [],
            billetes: [],
        };

        let amtRemaining = parseInt(String(amount));
        let done = false;
        const safety = 100;
        let tries = 0;
        while (amtRemaining > 0 && !done) {
            if (tries >= safety) {
                break;
            }

            available.billetes.forEach((billete) => {
                if (done) {
                    return;
                }

                const { multiplier, value, remaining } =
                    Cashdro.checkPiecesToSubstract(billete, amtRemaining);

                if (multiplier > 0) {
                    result.billetes.push({
                        level: multiplier,
                        value,
                    });
                }

                amtRemaining = remaining;

                if (amtRemaining === 0) {
                    done = true;
                }
            });

            if (done) {
                break;
            }

            available.monedas.forEach((moneda) => {
                if (done) {
                    return;
                }

                const { multiplier, value, remaining } =
                    Cashdro.checkPiecesToSubstract(moneda, amtRemaining);

                if (multiplier > 0) {
                    result.monedas.push({
                        level: multiplier,
                        value,
                    });
                }

                amtRemaining = remaining;

                if (remaining === 0) {
                    done = true;
                }
            });
            tries++;
        }

        return result;
    }

    static checkPiecesToSubstract(
        piece: Piece,
        remainingAmt: number,
    ): { multiplier: number; value: number; remaining: number } {
        // let remaining = remainingAmt;

        let multiplier = 0;
        let value = 0;
        let remaining = parseInt(String(remainingAmt));

        const pieceValue = parseInt(String(piece.value));

        if (remaining % pieceValue === 0) {
            // si es múltiplo exacto
            multiplier = remaining / pieceValue;
            remaining -= pieceValue * multiplier;
            value = pieceValue;
        } else if (pieceValue < remaining) {
            // si solo es mayor, pues la restamos y palante
            remaining -= pieceValue;
            multiplier = 1;
            value = pieceValue;
        }

        return { multiplier, value, remaining };
    }

    static validRefundPieces(
        pieces: AvailablePieces,
        amtToCheck: number | string,
    ): boolean {
        let amount = parseInt(String(amtToCheck));

        pieces.billetes.forEach((piece: any) => {
            amount -= Number(piece.value) * piece.level;
        });

        pieces.monedas.forEach((piece: any) => {
            amount -= Number(piece.value) * piece.level;
        });

        if (amount === 0) {
            console.log('refund pieces ok');
        }

        return amount === 0;
    }

    static saleOperationSuccessful(operation: Operation | null): {
        success: boolean;
        error: string | number | null;
    } {
        let success = false;
        let error: string | number | null = null;

        if (!operation || operation.state !== 'F') {
            return { success, error: OP_ERROR.UNAVAILABLE };
        }

        const total = parseInt(operation.total || '0');
        const totalin = parseInt(operation.totalin || '0');
        const totalout = parseInt(operation.totalout || '0');
        const amountchangenotavailable = parseInt(
            operation.amountchangenotavailable || '0',
        );

        if (
            operation.withError === 'true' ||
            (operation.withError && operation.withError !== 'false')
        ) {
            // error de la maquinaria interna
            error = OP_ERROR.HARDWARE;
        } else if (amountchangenotavailable) {
            // no había cambio
            error = OP_ERROR.NO_CHANGE;
        } else if (totalin === 0 && totalout === 0) {
            // operación cancelada / finalizada a manisqui
            error = OP_ERROR.CANCELLED;
        } else if (total === totalin + totalout) {
            // operación normal con devolución de cambio correcta
            success = true;
        }

        return { success, error };
    }

    async confirmRefund(
        opId: string,
        { monedas = [], billetes = [] }: AvailablePieces,
    ): Promise<CashdroResponse> {
        return this.finishOperation(
            opId,
            {
                /* La estructura de este parámetro de verdad tiene que ser así de cutre */
                Piezas: {
                    pieza: monedas
                        .map((piece: any) => ({
                            valor: piece.value.toString(),
                            nivel: piece.level.toString(),
                            isBillete: 'false',
                        }))
                        .concat(
                            billetes.map((piece: any) => ({
                                valor: piece.value.toString(),
                                nivel: piece.level.toString(),
                                isBillete: 'true',
                            })),
                        ) as unknown as PieceToFinish[],
                },
            },
            constants.cashdro.op.EXCUTE_WITHDRAWAL,
        );
    }
}
